'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { firebaseAuthService, RecaptchaVerifier } from '@/core/services';
import { useToast } from '@/core/presentation/components/Toast';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import type { LoginFormProps } from '@/features/authentication/presentation/components/LoginForm';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { useReminderSettings } from '@/features/app/reminder-settings/presentation/context/ReminderSettingsContext';
import { loadSessionHomePath } from '@/features/authentication/presentation/composables/utils/session-navigation';
import {
  ONBOARDING_PHONE_VERIFY_CODE_RESENT_TOAST,
  ONBOARDING_PHONE_VERIFY_CODE_SENT_TOAST,
} from '../constants/onboarding.constants';
import { LOGIN_FORM_SUCCESS_TOAST_MESSAGE } from '../constants/login.constants';
import { LOGIN_BRIDGE } from '../constants/login.constants';

export function useLoginBridge(): LoginFormProps {
  const navigate = useNavigate();
  const toast = useToast();
  const { state: authState, events } = useAuth();
  const { loading: authLoading, error } = authState;
  const { events: userEvents } = useUser();
  const { events: subscriptionEvents } = useSubscription();
  const { events: reminderEvents } = useReminderSettings();

  const recaptchaVerifierRef = useRef<InstanceType<typeof RecaptchaVerifier> | null>(null);
  /** Enquanto SMS foi enviado e o OTP ainda não concluiu a UX no `LoginForm`, não redirecionar ao detectar `user`. */
  const smsOtpFlowPendingRef = useRef(false);

  useEffect(() => {
    if (!authState.authReady) return;
    /** `onAuthStateChanged` pode atrasar vs. `currentUser` logo após `verifyOtp`. */
    const uid = authState.user?.uid ?? firebaseAuthService.getAuth().currentUser?.uid;
    if (!uid) return;
    if (smsOtpFlowPendingRef.current) return;
    let cancelled = false;
    void (async () => {
      const path = await loadSessionHomePath({
        uid,
        getUserById: userEvents.getUserById,
        getSubscriptionByUserId: subscriptionEvents.getSubscriptionByUserId,
        getReminderSettingsByUserId: reminderEvents.getByUserId,
      });
      if (cancelled) return;
      navigate(path, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [
    authState.authReady,
    authState.user?.uid,
    navigate,
    userEvents,
    subscriptionEvents,
    reminderEvents,
  ]);

  useEffect(() => {
    return () => {
      try {
        recaptchaVerifierRef.current?.clear();
      } catch {
        // noop: widget / DOM pode já estar inválido no unmount
      }
      recaptchaVerifierRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!error) return;
    toast.error(error);
    events.clearError();
  }, [error, toast, events]);

  const getOrCreateRecaptchaVerifier = useCallback(() => {
    if (recaptchaVerifierRef.current) return recaptchaVerifierRef.current;
    const auth = firebaseAuthService.getAuth();
    recaptchaVerifierRef.current = new RecaptchaVerifier(auth, LOGIN_BRIDGE.RECAPTCHA_CONTAINER_ID, {
      size: LOGIN_BRIDGE.RECAPTCHA_SIZE,
    });
    return recaptchaVerifierRef.current;
  }, []);

  const onPhoneInputChange = useCallback(() => {
    events.clearError();
  }, [events]);

  const onRequestSendCode = useCallback(
    async (whatsappNationalDigits: string) => {
      if (whatsappNationalDigits.length < LOGIN_BRIDGE.MIN_NATIONAL_DIGITS) return false;
      const e164 = `${LOGIN_BRIDGE.E164_BR_PREFIX}${whatsappNationalDigits}`;
      const verifier = getOrCreateRecaptchaVerifier();
      const sent = await events.sendOtp(e164, verifier);
      if (sent) {
        smsOtpFlowPendingRef.current = true;
        toast.success(ONBOARDING_PHONE_VERIFY_CODE_SENT_TOAST);
      }
      return sent;
    },
    [events, getOrCreateRecaptchaVerifier, toast]
  );

  const onResendCode = useCallback(
    async (whatsappNationalDigits: string) => {
      if (whatsappNationalDigits.length < LOGIN_BRIDGE.MIN_NATIONAL_DIGITS) return false;
      const e164 = `${LOGIN_BRIDGE.E164_BR_PREFIX}${whatsappNationalDigits}`;
      const verifier = getOrCreateRecaptchaVerifier();
      const sent = await events.resendOtp(e164, verifier);
      if (sent) {
        smsOtpFlowPendingRef.current = true;
        toast.success(ONBOARDING_PHONE_VERIFY_CODE_RESENT_TOAST);
      }
      return sent;
    },
    [events, getOrCreateRecaptchaVerifier, toast]
  );

  const onVerifyCode = useCallback(
    async (code: string) => {
      const ok = await events.verifyOtp(code);
      if (ok) toast.success(LOGIN_FORM_SUCCESS_TOAST_MESSAGE);
      return ok;
    },
    [events, toast]
  );

  const onChangeNumberStep = useCallback(() => {
    smsOtpFlowPendingRef.current = false;
    events.clearError();
  }, [events]);

  const onGoogleClick = useCallback(() => {
    smsOtpFlowPendingRef.current = false;
    void events.loginWithGoogle();
  }, [events]);

  const onFacebookClick = useCallback(() => {
    smsOtpFlowPendingRef.current = false;
    void events.loginWithMeta();
  }, [events]);

  const onVerified = useCallback(() => {
    smsOtpFlowPendingRef.current = false;
    const uid =
      authState.user?.uid ?? firebaseAuthService.getAuth().currentUser?.uid ?? undefined;
    if (!uid) return;
    void (async () => {
      const path = await loadSessionHomePath({
        uid,
        getUserById: userEvents.getUserById,
        getSubscriptionByUserId: subscriptionEvents.getSubscriptionByUserId,
        getReminderSettingsByUserId: reminderEvents.getByUserId,
      });
      navigate(path, { replace: true });
    })();
  }, [
    authState.user?.uid,
    navigate,
    userEvents,
    subscriptionEvents,
    reminderEvents,
  ]);

  return {
    authLoading,
    onRequestSendCode,
    onResendCode,
    onVerifyCode,
    onPhoneInputChange,
    onChangeNumberStep,
    onGoogleClick,
    onFacebookClick,
    onVerified,
  };
}
